import { useMediaQuery } from "./use-media-query";

/**
 * Custom hook to detect if the current viewport is mobile size (< 768px)
 * Uses the standard Tailwind CSS mobile breakpoint
 *
 * @returns boolean - true if viewport is mobile size, false otherwise
 */
export function useIsMobile(): boolean {
	return useMediaQuery("(max-width: 767px)");
}

/**
 * Custom hook to detect if the current viewport is desktop size (>= 768px)
 *
 * @returns boolean - true if viewport is desktop size, false otherwise
 */
export function useIsDesktop(): boolean {
	return useMediaQuery("(min-width: 768px)");
}

/**
 * Custom hook to detect if the current viewport is tablet size (768px - 1023px)
 *
 * @returns boolean - true if viewport is tablet size, false otherwise
 */
export function useIsTablet(): boolean {
	return useMediaQuery("(min-width: 768px) and (max-width: 1023px)");
}

/**
 * Custom hook that returns mobile, tablet, and desktop states
 * Useful when you need multiple breakpoint states in the same component
 *
 * @returns object with isMobile, isTablet, and isDesktop booleans
 */
export function useBreakpoint() {
	const isMobile = useIsMobile();
	const isTablet = useIsTablet();
	const isDesktop = useIsDesktop();

	return {
		isMobile,
		isTablet,
		isDesktop,
	};
}

/**
 * Custom hook to detect if the device has a coarse pointer (touch device)
 * This is more accurate than checking for "ontouchstart" as it detects the primary input method
 *
 * Use this for detecting mobile/touch devices that need tap interactions
 * instead of hover interactions.
 *
 * @returns boolean - true if the primary pointer is coarse (touch), false otherwise
 * @example
 * const isTouchDevice = useIsTouchDevice();
 * if (isTouchDevice) {
 *   // Show tooltip on tap
 * } else {
 *   // Show tooltip on hover
 * }
 */
export function useIsTouchDevice(): boolean {
	return useMediaQuery("(pointer: coarse)");
}
